import { C, F, M, weekdayLabel, dayMonthLabel, zonedDayRangeUtc } from './theme';
import type { Aircraft, Asset, Base, Booking, RowFilters, Staff } from '../../lib/opsBoardTypes';

interface Props {
  base: Base | null; // null = off-base / other group
  allBases: boolean;
  showHeader: boolean;
  bookings: Booking[]; // week range, cancelled removed (all bases)
  aircraft: Aircraft[];
  assets: Asset[];
  staff: Staff[];
  bases: Base[];
  weekDates: string[]; // 7 YYYY-MM-DD
  today: string;
  filters: RowFilters;
  onDayClick: (dateStr: string) => void;
}

const MAX_BOOKINGS = 5; // utilisation scale

interface Row { key: string; title: string; subtitle: string; count: (dateStr: string) => number; }

function bookingEndMs(b: Booking): number {
  const s = new Date(b.block_start_at!).getTime();
  if (b.arr_time_planned) return new Date(b.arr_time_planned).getTime();
  return s + (b.activity_mins ?? 60) * 60_000;
}

export default function WeekView(props: Props) {
  const { base, allBases, showHeader, bookings, aircraft, assets, staff, bases, weekDates, today, filters, onDayClick } = props;

  const tenantIcaos = new Set(bases.map((b) => b.icao).filter((x): x is string => !!x));
  const baseById = new Map(bases.map((b) => [b.id, b]));
  const baseByIcao = new Map(bases.filter((b) => b.icao).map((b) => [b.icao as string, b]));
  const offBase = base === null;
  const instructorBaseId = (s: Staff): string | null => s.current_base_id ?? s.home_base_id ?? null;
  const aircraftResidentBaseIcao = (a: Aircraft): string | null => (a.current_base_icao && tenantIcaos.has(a.current_base_icao) ? a.current_base_icao : null);

  // Multi-day aware: does the booking overlap the calendar day `d` (in its own tz)?
  const onDay = (b: Booking, d: string): boolean => {
    if (!b.block_start_at) return false;
    const { from, to } = zonedDayRangeUtc(d, b.dep_timezone);
    const s = new Date(b.block_start_at).getTime();
    const e = bookingEndMs(b);
    return e > new Date(from).getTime() && s < new Date(to).getTime();
  };
  const acCount = (reg: string, d: string, atBase?: string) => bookings.filter((b) => onDay(b, d) && (!atBase || b.base_id === atBase) && b.asset_allocations.some((a) => a.aircraft_registration === reg)).length;
  const assetCount = (id: string, d: string, atBase?: string) => bookings.filter((b) => onDay(b, d) && (!atBase || b.base_id === atBase) && b.asset_allocations.some((a) => a.asset_id === id)).length;
  const staffCount = (id: string, d: string, atBase?: string) => bookings.filter((b) => onDay(b, d) && (!atBase || b.base_id === atBase) && b.pilot_id === id).length;
  // External aircraft (Phase 1) — count of bookings carrying ≥1 external allocation on day d.
  const externalCount = (d: string, atBase?: string) => bookings.filter((b) => onDay(b, d) && (!atBase || b.base_id === atBase) && b.asset_allocations.some((a) => a.external_aircraft_source)).length;
  const hasAtBase = (predicate: (b: Booking) => boolean) => bookings.some((b) => base && b.base_id === base.id && predicate(b));

  // Aircraft for this section.
  let sectionAircraft: Aircraft[];
  let visitingAircraft: Aircraft[] = [];
  if (offBase) {
    sectionAircraft = aircraft.filter((a) => aircraftResidentBaseIcao(a) === null);
    if (!allBases) sectionAircraft = sectionAircraft.filter((a) => weekDates.some((d) => acCount(a.registration, d) > 0));
  } else {
    sectionAircraft = aircraft.filter((a) => aircraftResidentBaseIcao(a) === base!.icao);
    visitingAircraft = aircraft.filter((a) => { const h = aircraftResidentBaseIcao(a); return h !== null && h !== base!.icao && hasAtBase((b) => b.asset_allocations.some((x) => x.aircraft_registration === a.registration)); });
  }

  const rows: Row[] = [];

  if (filters.aircraft) {
    for (const ac of sectionAircraft) {
      const degraded = !!ac.capability_current && ac.capability_current !== ac.capability_normal;
      // Respect commencement: don't flag U/S before maintenance starts (unserviceable_from after the
      // viewed week = not yet U/S → show the normal subtitle). null = ad-hoc/defect U/S (U/S from now).
      // (Date-string compare carries the same ≤1-day UTC tolerance as elsewhere — acceptable here.)
      const commenced = !ac.unserviceable_from || ac.unserviceable_from.slice(0, 10) <= weekDates[weekDates.length - 1];
      const sub = ac.serviceable === false && commenced ? '⚠ unserviceable' : degraded ? `⚠ ${ac.capability_current}` : offBase ? (ac.current_base_icao ?? 'off-base') : (ac.model ?? ac.aircraft_category);
      rows.push({ key: `ac-${ac.registration}`, title: ac.registration, subtitle: sub, count: (d) => acCount(ac.registration, d) });
    }
    for (const ac of visitingAircraft) {
      const home = aircraftResidentBaseIcao(ac);
      const homeName = (home && baseByIcao.get(home)?.name) || home || 'elsewhere';
      rows.push({ key: `acv-${ac.registration}`, title: ac.registration, subtitle: `visiting — ${homeName}`, count: (d) => acCount(ac.registration, d, base!.id) });
    }
    // Synthetic External row — only when an external booking falls somewhere in the visible week.
    if (!offBase && weekDates.some((d) => externalCount(d, base!.id) > 0)) {
      rows.push({ key: 'ext-row', title: 'External', subtitle: 'Cross-hire / client', count: (d) => externalCount(d, base!.id) });
    }
  }

  if (!offBase && filters.instructors) {
    const residentStaff = staff.filter((s) => instructorBaseId(s) === base!.id);
    const visitingStaff = staff.filter((s) => instructorBaseId(s) !== base!.id && hasAtBase((b) => b.pilot_id === s.id));
    for (const s of residentStaff) rows.push({ key: `st-${s.id}`, title: s.pilot_code ?? s.preferred_name ?? s.full_name, subtitle: s.role ?? 'Instructor', count: (d) => staffCount(s.id, d) });
    for (const s of visitingStaff) {
      const home = instructorBaseId(s);
      const homeName = (home && baseById.get(home)?.name) || 'elsewhere';
      rows.push({ key: `stv-${s.id}`, title: s.pilot_code ?? s.preferred_name ?? s.full_name, subtitle: `visiting — ${homeName}`, count: (d) => staffCount(s.id, d, base!.id) });
    }
  }

  if (!offBase && filters.assets) {
    const residentAssets = assets.filter((a) => a.base_id === base!.id);
    const visitingAssets = assets.filter((a) => a.base_id !== base!.id && hasAtBase((b) => b.asset_allocations.some((x) => x.asset_id === a.id)));
    for (const as of residentAssets) rows.push({ key: `as-${as.id}`, title: as.short_code ?? as.name, subtitle: as.category ?? 'Asset', count: (d) => assetCount(as.id, d) });
    for (const as of visitingAssets) {
      const homeName = baseById.get(as.base_id)?.name ?? 'elsewhere';
      rows.push({ key: `asv-${as.id}`, title: as.short_code ?? as.name, subtitle: `visiting — ${homeName}`, count: (d) => assetCount(as.id, d, base!.id) });
    }
  }

  if (offBase && rows.length === 0) return null;

  return (
    <div style={{ marginBottom: allBases ? 18 : 0, overflowX: 'auto' }}>
      {showHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 6px' }}>
          <span style={{ background: base?.ui_color ?? C.slate, color: '#fff', borderRadius: 5, padding: '3px 10px', fontSize: 12, fontWeight: 700, fontFamily: F }}>
            {offBase ? 'Off-base / Other' : base!.name}
          </span>
          {!offBase && base?.icao && <span style={{ fontSize: 10, color: C.textMuted, fontFamily: M }}>{base.icao}</span>}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: F }}>
        <thead>
          <tr>
            <th style={{ width: 140, padding: '8px 10px', textAlign: 'left', background: C.navy, color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: '6px 0 0 0' }}>
              {offBase ? 'Off-base / Other' : 'Resource'}
            </th>
            {weekDates.map((d, i) => (
              <th key={d} style={{ padding: '8px 6px', textAlign: 'center', background: C.navy, color: '#fff', fontSize: 11, fontWeight: 700, borderRight: '1px solid rgba(255,255,255,0.1)', ...(i === weekDates.length - 1 ? { borderRadius: '0 6px 0 0' } : {}) }}>
                <div>{weekdayLabel(d)}</div>
                <div style={{ fontSize: 9, fontWeight: 400, opacity: 0.7 }}>{dayMonthLabel(d)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={weekDates.length + 1} style={{ padding: 16, textAlign: 'center', color: C.textMuted, fontSize: 12, border: `1px solid ${C.border}` }}>No resources to show.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.key}>
              <td style={{ padding: '8px 10px', background: C.surface, border: `1px solid ${C.border}`, verticalAlign: 'middle' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, fontFamily: M }}>{r.title}</div>
                <div style={{ fontSize: 9, color: C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130 }}>{r.subtitle}</div>
              </td>
              {weekDates.map((d) => {
                const n = r.count(d);
                const util = n / MAX_BOOKINGS;
                const isToday = d === today;
                return (
                  <td key={d} onClick={() => onDayClick(d)} style={{ padding: '6px', textAlign: 'center', background: isToday ? '#eff6ff' : C.surface, border: `1px solid ${isToday ? C.accentBorder : C.border}`, cursor: 'pointer', verticalAlign: 'middle' }}>
                    {n > 0 ? (
                      <>
                        <div style={{ fontSize: 18, fontWeight: 700, color: util >= 0.8 ? C.red : util >= 0.6 ? C.amber : C.green, fontFamily: M, lineHeight: 1 }}>{n}</div>
                        <div style={{ fontSize: 8, color: C.textMuted, marginTop: 2 }}>booking{n === 1 ? '' : 's'}</div>
                        <div style={{ height: 3, borderRadius: 2, marginTop: 4, background: util >= 0.8 ? C.redLight : util >= 0.6 ? C.amberLight : C.greenLight }}>
                          <div style={{ height: '100%', borderRadius: 2, width: `${Math.min(100, util * 100)}%`, background: util >= 0.8 ? C.red : util >= 0.6 ? C.amber : C.green }} />
                        </div>
                      </>
                    ) : (
                      <div style={{ fontSize: 11, color: C.border }}>—</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
